import { ErrorEvent } from '@sentry/types'
import { Breadcrumb, EventHint } from '@sentry/react'

interface XHRError {
    url: string
    method: string
    statusCode: number
}

const unknownXHRError: XHRError = {
    url: '<unknown>',
    method: '<unknown>',
    statusCode: 0,
}

const UUIDPattern = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g

/**
 * Some URL may contain UUID e.g., https://api.orderwire.works/api/location/menus/pos/761ab73b-7bb6-4597-be15-4271eb1f6b10
 * To use URL for grouping Sentry event. We have to normalize these URL.
 * This function will replace all UUID part with '{uuid}'
 *  - With above URL this will result: https://api.orderwire.works/api/location/menus/pos/{uuid}
 * @param url
 * @returns
 */
const normalizeURL = (url: string | null | undefined) => {
    if (!url) return ''

    return url.replaceAll(UUIDPattern, '{uuid}')
}

const isPromiseRejectionError = (event: ErrorEvent): boolean => {
    if (
        !event.exception ||
        !event.exception.values ||
        event.exception.values.length === 0
    )
        return false

    return (
        event.exception.values[0].type === 'UnhandledRejection' &&
        (event.exception.values[0].value || '').includes(
            'Non-Error promise rejection',
        )
    )
}

const appendFingerPrint = (event: ErrorEvent, fingerprint: string[]) => {
    if (!event.fingerprint) {
        event.fingerprint = fingerprint
        return
    }

    event.fingerprint = [...event.fingerprint, ...fingerprint]
}

const getLatestXHRErrorFromBreadCrumbs = (
    breadcrumbs: Breadcrumb[] | undefined,
): XHRError | null => {
    if (!breadcrumbs || breadcrumbs.length === 0) return null

    let found = false

    // Sentry's Breadcrumbs is sorted (ASC) by occurring time
    // So, we start from last item
    let i = breadcrumbs.length - 1

    let url = ''
    let method = ''
    let statusCode = 200

    while (!found && i >= 0) {
        const breadcrumbRecord = breadcrumbs[i]

        if (
            breadcrumbRecord.category === 'xhr' &&
            breadcrumbRecord.type === 'http' &&
            breadcrumbRecord.data
        ) {
            const data = breadcrumbRecord.data
            url = normalizeURL(data['url'] || '')
            method = data['method'] || ''
            statusCode = data['status_code'] || 0
        }
        // sometimes, the latest HTTP-typed breadcrumb can be HTTP 200 result, which is not the original that trigger the error
        // this occured once in POS-8035 (https://orderwire.atlassian.net/browse/POS-8035)
        // to prevent this kind, we dig down until found Non-200 response status
        if (statusCode !== 200) {
            found = true
        }

        i--
    }

    if (!found) return null

    return { url, method, statusCode }
}

export const enrichSentryErrorEvent = (
    event: ErrorEvent,
    hint: EventHint,
): void => {
    const isUnhandledPromiseError = isPromiseRejectionError(event)
    if (isUnhandledPromiseError) {
        let xhrError = unknownXHRError

        // The `originalException.httpMetadata` is produced in our codebase
        // Please see function `attachHttpMetadataToError` in `httpRequest\index.ts` for more implementation details
        if (
            hint?.originalException &&
            (hint?.originalException as any).httpMetadata
        ) {
            const httpMetadata = (hint!.originalException! as any)
                .httpMetadata as any

            // skip if no `url` present
            if (httpMetadata.url)
                xhrError = {
                    url: httpMetadata.url,
                    statusCode: httpMetadata.status,
                    method: httpMetadata.method,
                }
        }

        // if no `httpMetadata` presented in original exception object
        // We use latest xhr error in the Sentry breadcrumbs as XHR Error object
        if (xhrError === unknownXHRError) {
            xhrError =
                getLatestXHRErrorFromBreadCrumbs(event.breadcrumbs) ||
                unknownXHRError
        }

        if (xhrError !== unknownXHRError) {
            event.fingerprint = []
            event.exception!.values![0].type = 'UnhandledPromise'
            event.exception!.values![0].value = `Unhandled promise error. ${xhrError.method} ${xhrError.url} with status code ${xhrError.statusCode}.`

            appendFingerPrint(event, [
                'UnhandledPromise',
                xhrError.method,
                xhrError.statusCode.toString(),
                xhrError.url,
            ])
        }
    }

    // stringify `additionValue` (if exists)
    if ((event.extra?.__serialized__ as any)?.additionValue) {
        ;(event.extra!.__serialized__ as any).additionValue = JSON.stringify(
            (event.extra!.__serialized__ as any).additionValue,
        )
    }
}
